// VendorScanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Camera, User, AlertCircle, Upload, UtensilsCrossed } from 'lucide-react';
import './VendorScanner.css';

const VendorScanner = () => {
  const [playerInfo, setPlayerInfo] = useState(null);
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const [userPhotoUrls, setUserPhotoUrls] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [mealAdded, setMealAdded] = useState(false);
  const [isAddingMeal, setIsAddingMeal] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  const getUserDisplayName = (userData) => {
    const firstName = userData.firstName || userData.FirstName || '';
    const lastName = userData.lastName || userData.LastName || '';
    const fullName = userData.fullName || userData.FullName || '';

    if (firstName || lastName) return `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    return userData.name || userData.userName || 'Unknown User';
  };

  const tryLoadPhoto = (userId) => {
    const extensions = ['png', 'jpg', 'jpeg'];
    const baseUrl = 'https://api.asterickx.com/uploads/members/photos';
    return extensions.map(ext => `${baseUrl}/${userId}.${ext}`);
  };

  const fetchPlayerInfo = async (qrData) => {
    try {
      let userId = qrData;

      if (qrData.includes('http') || qrData.includes('://')) {
        try {
          const url = new URL(qrData);
          const urlUserId = url.searchParams.get('id');
          if (urlUserId) {
            userId = urlUserId;
          } else {
            const pathParts = url.pathname.split('/').filter(p => p);
            userId = pathParts[pathParts.length - 1] || qrData;
          }
        } catch (urlError) {
          console.log('Not a valid URL, using raw data');
        }
      }

      console.log('🎯 Fetching user with ID:', userId);

      const token = localStorage.getItem('Token') || localStorage.getItem('token');
      let user = null;

      try {
        const membersApiUrl = `https://api.asterickx.com/api/Members`;
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(membersApiUrl, {
          method: 'GET',
          headers: headers
        });

        if (response.ok) {
          const members = await response.json();
          user = members.find(m =>
            m.id === userId ||
            m.id?.toLowerCase() === userId.toLowerCase() ||
            m.id?.toUpperCase() === userId.toUpperCase()
          );
        }
      } catch (err) {
        console.warn('⚠️ Failed to fetch members list:', err);
      }

      if (!user) {
        try {
          const individualApiUrl = `https://api.asterickx.com/api/Members/${userId}`;
          const headers = { 'Content-Type': 'application/json' };
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const response = await fetch(individualApiUrl, {
            method: 'GET',
            headers: headers
          });

          if (response.ok) {
            user = await response.json();
          }
        } catch (err) {
          console.warn('⚠️ Failed to fetch individual member:', err);
        }
      }

      if (!user) {
        throw new Error('User not found in database');
      }

      console.log('👤 Full playerInfo from API:', user);
      console.log('👤 playerInfo keys:', Object.keys(user));

      setPlayerInfo(user);
      setError('');
      setMealAdded(false);

      if (user.photoPath) {
        console.log('📸 Photo path from API:', user.photoPath);
        if (user.photoPath.startsWith('http')) {
          setUserPhotoUrls([user.photoPath]);
          setCurrentPhotoIndex(0);
        } else {
          const fullPath = `https://api.asterickx.com${user.photoPath}`;
          setUserPhotoUrls([fullPath]);
          setCurrentPhotoIndex(0);
        }
      } else {
        const photoUrls = tryLoadPhoto(user.id);
        setUserPhotoUrls(photoUrls);
        setCurrentPhotoIndex(0);
        console.log('📸 No photoPath in API, trying constructed URLs:', photoUrls);
      }

      stopCamera();
    } catch (err) {
      console.error('❌ Error fetching user:', err);
      setError(`Member not found. Please verify the QR code and try again.`);
      setTimeout(() => setError(''), 5000);
    }
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current || !window.jsQR) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) return;

    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    if (canvas.width === 0 || canvas.height === 0) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    try {
      const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "attemptBoth",
      });

      if (code && code.data) {
        console.log('✅ QR Code detected:', code.data);
        fetchPlayerInfo(code.data);
      }
    } catch (err) {
      console.error('❌ QR scan error:', err);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        if (window.jsQR) {
          const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "attemptBoth",
          });

          if (code && code.data) {
            fetchPlayerInfo(code.data);
          } else {
            setError('No QR code found in image. Please ensure the QR code is clearly visible and try again.');
            setTimeout(() => setError(''), 5000);
          }
        }
      };

      img.onerror = () => {
        setError('Failed to load image. Please try another photo.');
        setTimeout(() => setError(''), 3000);
      };

      img.src = event.target.result;
    };

    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
      setTimeout(() => setError(''), 3000);
    };

    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      setError('');

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera is not supported in this browser. Please use the upload option or try Chrome/Firefox/Safari.');
        return;
      }

      let constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        constraints = { video: true };
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => {
            setIsScanning(true);
            scanIntervalRef.current = setInterval(scanQRCode, 250);
          }).catch(err => {
            setError('Failed to start video. Please try the upload option.');
          });
        };
      }
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access or use the upload option.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please use the upload option to scan your ID card.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is in use. Please close other apps or use the upload option.');
      } else {
        setError('Camera error. Please use the upload option to scan your ID card.');
      }
    }
  };

  const stopCamera = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsScanning(false);
  };

  const handleReset = () => {
    setPlayerInfo(null);
    setUserPhotoUrls([]);
    setCurrentPhotoIndex(0);
    setError('');
    setMealAdded(false);
    startCamera();
  };

  const handlePhotoError = () => {
    const currentUrl = userPhotoUrls[currentPhotoIndex];
    console.log(`❌ Photo failed to load: ${currentUrl}`);

    if (userPhotoUrls.length === 1 && playerInfo) {
      console.log('API photo failed, trying constructed URLs with all extensions...');
      const photoUrls = tryLoadPhoto(playerInfo.id);
      setUserPhotoUrls(photoUrls);
      setCurrentPhotoIndex(0);
      return;
    }

    if (currentPhotoIndex < userPhotoUrls.length - 1) {
      console.log(`Trying next extension (${currentPhotoIndex + 1}/${userPhotoUrls.length})...`);
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    } else {
      console.log('❌ All photo formats failed, showing default icon');
    }
  };

  const showAlert = (icon, title, text, timer = null) => {
    if (window.Swal) {
      const config = { icon, title, text, confirmButtonColor: '#2563eb' };
      if (timer) {
        config.timer = timer;
        config.showConfirmButton = false;
      }
      window.Swal.fire(config);
    } else {
      alert(`${title}: ${text}`);
    }
  };

  const handleAddMeal = async () => {
    if (!playerInfo) {
      showAlert('error', 'Error', 'User information not found.');
      return;
    }

    // Log full playerInfo so we can see all available fields
    console.log('👤 Full playerInfo:', JSON.stringify(playerInfo, null, 2));
    console.log('👤 playerInfo keys:', Object.keys(playerInfo));

    // Try every possible casing/naming for registration number
    const registrationNumber =
      playerInfo.registrationNumber ||
      playerInfo.RegistrationNumber ||
      playerInfo.registration_number ||
      playerInfo.regNumber ||
      playerInfo.RegNumber ||
      playerInfo.regno ||
      playerInfo.RegNo ||
      playerInfo.reg_no ||
      playerInfo.membershipNumber ||
      playerInfo.MembershipNumber ||
      playerInfo.membership_number ||
      playerInfo.memberId ||
      playerInfo.MemberId ||
      playerInfo.member_id ||
      playerInfo.id; // last resort fallback to member ID

    console.log('📋 Resolved registrationNumber:', registrationNumber);

    if (!registrationNumber) {
      console.error('❌ No registration number found. playerInfo:', playerInfo);
      showAlert('error', 'Missing Information', 'Registration number not found for this user.');
      return;
    }

    setIsAddingMeal(true);

    try {
      const token = localStorage.getItem('Token') || localStorage.getItem('token');
      const tournamentId = 'afbd5d83-4524-44d9-8d44-9ff0d39d9d2f';

      const mealData = {
        tournamentId: tournamentId,
        registrationNumber: registrationNumber
      };

      console.log('🍽️ Sending meal data:', JSON.stringify(mealData, null, 2));
      console.log('🔐 Token present:', !!token);
      console.log('🔐 Token value (first 20 chars):', token ? token.substring(0, 20) + '...' : 'NONE');

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else {
        console.warn('⚠️ No authorization token found in localStorage');
      }

      console.log('📡 Calling API: POST https://api.asterickx.com/api/Meals/scan');

      const response = await fetch('https://api.asterickx.com/api/Meals/scan', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(mealData)
      });

      console.log('📥 Response status:', response.status, response.statusText);
      console.log('📥 Response headers:', [...response.headers.entries()]);

      // Read raw text first for better debugging
      const rawText = await response.text();
      console.log('📥 Raw response body:', rawText);

      let responseData = {};
      try {
        responseData = JSON.parse(rawText);
        console.log('📥 Parsed response data:', responseData);
      } catch (e) {
        console.warn('⚠️ Response is not JSON. Raw text was:', rawText);
      }

      if (response.ok) {
        console.log('✅ Meal added successfully');
        setMealAdded(true);
        showAlert('success', 'Success!', 'Meal added successfully', 1500);

        setTimeout(() => {
          handleReset();
        }, 2000);
      } else {
        const errorMessage =
          responseData.message ||
          responseData.error ||
          responseData.title ||
          responseData.Message ||
          responseData.Error ||
          rawText ||
          `HTTP ${response.status}: Failed to add meal`;

        console.error('❌ API Error:', response.status, errorMessage);
        console.error('❌ Full response data:', responseData);

        const errorLower = errorMessage.toLowerCase();

        if (
          errorLower.includes('already') &&
          (errorLower.includes('consumed') ||
            errorLower.includes('scanned') ||
            errorLower.includes('taken') ||
            errorLower.includes('scan'))
        ) {
          showAlert('warning', 'Already Consumed', 'Meal Already Consumed For This Session');
        } else if (
          errorLower.includes('no') &&
          (errorLower.includes('session') ||
            errorLower.includes('meal') ||
            errorLower.includes('available'))
        ) {
          showAlert('info', 'No Meal Available', 'No Meal Available At This Time');
        } else if (
          errorLower.includes('session') &&
          (errorLower.includes('not available') ||
            errorLower.includes('not active') ||
            errorLower.includes('inactive'))
        ) {
          showAlert('info', 'No Meal Available', 'No Meal Available At This Time');
        } else if (
          errorLower.includes('unauthorized') ||
          response.status === 401
        ) {
          showAlert('error', 'Unauthorized', 'Your session has expired. Please log in again.');
        } else if (response.status === 404) {
          showAlert('error', 'Not Found', 'Meal endpoint not found. Please contact support.');
        } else {
          showAlert('error', `Error (${response.status})`, errorMessage);
        }
      }
    } catch (error) {
      // This catch only fires for network/fetch errors (CORS, no internet, etc.)
      console.error('❌ Network/Fetch error adding meal:', error);
      console.error('❌ Error name:', error.name);
      console.error('❌ Error message:', error.message);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        showAlert(
          'error',
          'Network Error',
          'Cannot reach the server. This may be a CORS issue or network problem. Check the browser console for details.'
        );
      } else {
        showAlert(
          'error',
          'Error',
          `Failed to add meal: ${error.message}. Please check your connection and try again.`
        );
      }
    } finally {
      setIsAddingMeal(false);
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';
    script.async = true;

    script.onload = () => {
      setLibraryLoaded(true);
    };

    script.onerror = () => {
      setError('Failed to load scanner. Please check your internet connection and refresh.');
    };

    document.head.appendChild(script);

    const sweetAlertScript = document.createElement('script');
    sweetAlertScript.src = 'https://cdn.jsdelivr.net/npm/sweetalert2@11';
    sweetAlertScript.async = true;
    document.head.appendChild(sweetAlertScript);

    const sweetAlertCSS = document.createElement('link');
    sweetAlertCSS.rel = 'stylesheet';
    sweetAlertCSS.href = 'https://cdn.jsdelivr.net/npm/sweetalert2@11/dist/sweetalert2.min.css';
    document.head.appendChild(sweetAlertCSS);

    return () => {
      if (document.head.contains(script)) document.head.removeChild(script);
      if (document.head.contains(sweetAlertScript)) document.head.removeChild(sweetAlertScript);
      if (document.head.contains(sweetAlertCSS)) document.head.removeChild(sweetAlertCSS);
    };
  }, []);

  useEffect(() => {
    if (libraryLoaded && !playerInfo) {
      const timer = setTimeout(() => {
        startCamera();
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [libraryLoaded, playerInfo]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const displayName = playerInfo ? getUserDisplayName(playerInfo) : '';

  return (
    <div className="vendor-scanner">
      <div className="container">
        {/* Header */}
        <div className="header-card">
          <h1 className="header-title">Vendor QR Scanner</h1>
          <p className="header-subtitle">Scan member ID cards to add meals</p>
        </div>

        {/* Scanner or Player Info */}
        {!playerInfo ? (
          <div className="scanner-card">
            {!libraryLoaded ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p className="loading-text">Loading scanner...</p>
              </div>
            ) : (
              <div className="scanner-content">
                <h2 className="scanner-title">
                  {isScanning ? 'Scanning ID Card...' : 'Ready to Scan'}
                </h2>
                <p className="scanner-subtitle">
                  Hold the ID card steady within the camera view
                </p>

                {/* Camera View */}
                <div className="camera-container">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="camera-video"
                  />

                  {isScanning && (
                    <div className="scanning-overlay">
                      <div className="scanning-box">
                        <div className="corner corner-tl"></div>
                        <div className="corner corner-tr"></div>
                        <div className="corner corner-bl"></div>
                        <div className="corner corner-br"></div>
                      </div>
                    </div>
                  )}

                  {!isScanning && (
                    <div className="start-camera-overlay">
                      <button onClick={startCamera} className="btn btn-camera">
                        <Camera className="icon" />
                        Start Camera
                      </button>
                    </div>
                  )}
                </div>

                <canvas ref={canvasRef} className="hidden-canvas" />

                {/* Upload Option */}
                <div className="upload-section">
                  <p className="upload-text">Camera not working? Upload a photo of the ID card</p>
                  <label htmlFor="id-upload" className="btn btn-upload">
                    <Upload className="icon" />
                    Upload ID Card Photo
                  </label>
                  <input
                    id="id-upload"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageUpload}
                    className="file-input"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="error-container">
                <div className="error-content">
                  <AlertCircle className="error-icon" />
                  <p className="error-text">{error}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="player-card">
            {/* Success Message */}
            {mealAdded && (
              <div className="success-banner">
                ✓ Meal Added Successfully!
              </div>
            )}

            {/* Player Info */}
            <div className="player-content">
              <div className="player-info">
                {/* Photo */}
                <div className="photo-container">
                  {userPhotoUrls.length > 0 && currentPhotoIndex < userPhotoUrls.length ? (
                    <img
                      key={currentPhotoIndex}
                      src={userPhotoUrls[currentPhotoIndex]}
                      alt="Profile"
                      className="photo-image"
                      onError={handlePhotoError}
                    />
                  ) : (
                    <User className="photo-default" />
                  )}
                </div>

                {/* Name */}
                <h2 className="player-name">{displayName}</h2>

                {/* Add Meal Button */}
                <button
                  onClick={handleAddMeal}
                  disabled={mealAdded || isAddingMeal}
                  className={`btn btn-meal ${mealAdded ? 'btn-meal-added' : ''} ${isAddingMeal ? 'btn-meal-loading' : ''}`}
                >
                  {isAddingMeal ? (
                    <>
                      <div className="btn-spinner"></div>
                      Adding Meal...
                    </>
                  ) : mealAdded ? (
                    <>
                      <UtensilsCrossed className="icon" />
                      Meal Added
                    </>
                  ) : (
                    <>
                      <UtensilsCrossed className="icon" />
                      Add Meal
                    </>
                  )}
                </button>

                {/* Scan Another Button */}
                {!mealAdded && (
                  <button onClick={handleReset} className="btn btn-scan-another">
                    Scan Another ID Card
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorScanner;